import { useId, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useProjects } from "@/domains/documents/hooks/useProjects";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ProjectCreateFieldLabel, ProjectStructureGuidance } from "./ProjectCreateFormPresentation";

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Building the new project belongs to. */
  buildingId: string;
  /**
   * Called with the new project's id after the create mutation succeeds — e.g.
   * to scope straight to it when creating from a project picker.
   */
  onCreated?: (projectId: number) => void;
}

/**
 * The create-project form, wherever a "New project" control offers it. Reuses
 * useProjects' create mutation, so toasts and ["building-projects", buildingId]
 * invalidation behave exactly like creating from the building dashboard.
 *
 * Several of these are mounted at once on a building page — the pickers' shared
 * one (CreateProjectDialogProvider) plus ProjectsEmptyState's and
 * DashboardProjectsGrid's own — so the field ids are per-instance (`useId`),
 * never the static strings they used to be. Two closed Radix dialogs keep their
 * content out of the DOM and so never collided in practice, but a duplicate id
 * is what breaks label association the moment that stops being true.
 */
export function CreateProjectDialog({
  open,
  onOpenChange,
  buildingId,
  onCreated,
}: CreateProjectDialogProps) {
  const { t } = useLanguage();
  const fieldId = useId();
  const nameId = `${fieldId}-project-name`;
  const descriptionId = `${fieldId}-project-description`;
  // `list: false`: this form only ever writes. Its owner keeps it mounted for
  // the whole building page (a mutate-level `onSuccess` dies with its
  // component), so the list query would run on every building route — and for
  // every user, including the ones without CREATE_PROJECTS who can never open
  // it. The create still invalidates the list for whoever is showing one.
  const { createProject, isCreating } = useProjects(buildingId, { list: false });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleCreate = () => {
    if (!name.trim()) return;
    createProject(
      {
        building_id: buildingId,
        name: name.trim(),
        description: description.trim() || undefined,
      },
      {
        // Close and clear only once the mutation succeeds — a failed create
        // keeps the dialog open with the typed values so the user can retry
        // (the hook-level onError already toasts the failure).
        onSuccess: (project) => {
          setName("");
          setDescription("");
          // Before the close, deliberately: an owner may read a close it did
          // not ask for as the user dismissing the dialog — Cancel and Esc
          // both work while a create is in flight — and drop the
          // follow-through it was holding. Reported first, the success close
          // can't be mistaken for that dismissal.
          onCreated?.(project.id);
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] max-w-2xl gap-0 overflow-y-auto p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pb-6 pt-6">
          <DialogTitle>{t.projects.create.modalTitle}</DialogTitle>
          <DialogDescription>{t.projects.create.modalSubtitle}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-6 pb-6">
          <div className="space-y-1.5">
            <ProjectCreateFieldLabel htmlFor={nameId} label={t.projects.create.modalNameLabel} />
            <Input
              id={nameId}
              aria-required="true"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.projects.create.modalNamePlaceholder}
            />
          </div>
          <div className="space-y-1.5">
            <ProjectCreateFieldLabel
              htmlFor={descriptionId}
              label={t.projects.create.modalDescriptionLabel}
              optionalHint={t.projects.create.modalOptionalLabel}
            />
            <Textarea
              id={descriptionId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t.projects.create.modalDescriptionPlaceholder}
              rows={3}
            />
          </div>
          <ProjectStructureGuidance>{t.projects.create.modalGuidance}</ProjectStructureGuidance>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleCreate} disabled={!name.trim() || isCreating}>
              {isCreating ? t.projects.create.creating : t.projects.create.modalSubmit}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
